import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Owner from "./Owner";
import {
  editApplication,
  getApplication,
  resolveApplicationManager,
  deleteApplication,
} from "../../actions";
import AdminGroup from "../groups/AdminGroup";
import { useNavigate } from "react-router-dom";
import { Descriptions, Button, Modal, Tooltip, Alert, Typography } from "antd";
import {
  EditOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import NewApplicationForm from "../forms/NewApplicationForm";
import PlaceholderLineLoader from "../PlaceholderLineLoader";
import { showErrorNotification } from "../../utils/index";
import { errorAction, types as at } from "@authzsvc/api-lib";
import moment from "moment";

const { Text } = Typography;

const ApplicationDetails = ({
  application,
  editApplication,
  getApplication,
  resolveApplicationManager,
  deleteApplication,
  editMode,
  setEditMode,
}) => {
  const [manager, setManager] = useState(undefined);
  const [loadingManager, setLoadingManager] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getManagerData = async () => {
      setLoadingManager(true);
      const managerResponse = await resolveApplicationManager(
        application.managerId,
      );
      if (managerResponse.payload?.data) {
        setManager(managerResponse.payload.data);
      }
      setLoadingManager(false);
    };
    if (!manager && application.managerId) {
      getManagerData();
    }
  }, [manager, application.managerId, resolveApplicationManager]);

  const handleEditApplication = async (app) => {
    const resp = await editApplication({
      ...application,
      ...app,
    });
    if (resp.type === errorAction(at.EDIT_APPLICATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await getApplication(application.id);
    setEditMode(false);
  };

  const handleDelete = async () => {
    setDeleteModalVisible(false);
    const resp = await deleteApplication(application);
    if (resp && resp.type === errorAction(at.DELETE_APPLICATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
      return;
    }
    navigate("/");
  };
  if (editMode) {
    return (
      <NewApplicationForm
        submitFunction={handleEditApplication}
        initialValues={{ ...application }}
        isEdit={true}
        setEditMode={setEditMode}
      />
    );
  }

  return (
    <>
      <Descriptions bordered column={1} size="middle">
        <Descriptions.Item label="Application Identifier">
          {application.applicationIdentifier}
        </Descriptions.Item>
        <Descriptions.Item label="Name">
          {application.displayName}
        </Descriptions.Item>
        <Descriptions.Item label="Home Page">
          <a
            href={application.homePage}
            target="_blank"
            rel="noopener noreferrer"
          >
            {application.homePage}
          </a>
        </Descriptions.Item>

        <Descriptions.Item label="Description">
          {application.description}
        </Descriptions.Item>

        {application.expirationDeadline && (
          <Descriptions.Item label="Expiration Deadline">
            {moment(application.expirationDeadline).format("DD/MM/YYYY")}
          </Descriptions.Item>
        )}
        {application.blockingDeadline && (
          <Descriptions.Item label="Blocking Deadline">
            {moment(application.blockingDeadline).format("DD/MM/YYYY")}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Owner">
          {application.ownerId ? (
            <Owner ownerId={application.ownerId} />
          ) : (
            "None"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Administrators">
          {application.administratorsId ? (
            <AdminGroup id={application.administratorsId} />
          ) : (
            "None"
          )}
        </Descriptions.Item>
        {manager && (
          <Descriptions.Item
            label={
              <>
                Managed By
                <Tooltip
                  title={`This application has been created through: ${manager.displayName}`}
                >
                  <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </>
            }
          >
            {loadingManager ? (
              <PlaceholderLineLoader count={1} />
            ) : (
              <span>
                {manager.displayName} ({manager.upn})
              </span>
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Category">
          {application.resourceCategory
            ? application.resourceCategory
            : "Not specified"}
        </Descriptions.Item>
        {application.creationTime && (
          <Descriptions.Item label="Creation Date">
            {moment(application.creationTime).format("YYYY/MM/DD HH:mm")}
          </Descriptions.Item>
        )}
      </Descriptions>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <Tooltip title="Edit application details">
          <Button
            id="editApplicationDetailsButton"
            color="blue"
            variant="filled"
            onClick={() => setEditMode(true)}
            icon={<EditOutlined />}
          >
            Edit
          </Button>
        </Tooltip>

        <Tooltip title="Delete application">
          <Button
            id="deleteApplicationButton"
            color="red"
            variant="filled"
            disabled={manager?.type === "Application"}
            onClick={() => setDeleteModalVisible(true)}
            icon={<DeleteOutlined />}
          >
            Delete
          </Button>
        </Tooltip>
      </div>

      {manager?.type === "Application" && (
        <Alert
          message="Application Deletion Not Allowed"
          description="Applications managed by other applications cannot be deleted! Please proceed to the site or service where this application is managed in order to delete it."
          type="warning"
          showIcon
          style={{ marginTop: 24 }}
        />
      )}

      <Modal
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDelete}
        okText="Delete Application"
        cancelText="Cancel"
        okButtonProps={{ danger: true, type: "primary" }}
        keyboard={true}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ExclamationCircleOutlined
              style={{ fontSize: 24, color: "#faad14" }}
            />
            <span>Delete Application: {application.displayName}</span>
          </div>
        }
        width={500}
        centered
        closable
      >
        <Text>
          Are you sure you want to delete this application? This action is
          irreversible.
        </Text>
      </Modal>
    </>
  );
};

ApplicationDetails.propTypes = {
  application: PropTypes.object,
  editApplication: PropTypes.func,
  getApplication: PropTypes.func,
  resolveApplicationManager: PropTypes.func,
  deleteApplication: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
};

export default connect(null, {
  editApplication,
  getApplication,
  resolveApplicationManager,
  deleteApplication,
})(ApplicationDetails);
